import { PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { BenchmarkMetricsRow } from 'pages/Benchmarking/common/BenchmarkMetricsRow';
import { MetricsChart } from 'components/MetricsChart';
import { FormattedMessage, useIntl } from 'react-intl';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { ColumnChartRenderer } from 'components/chart/renderer/ColumnChartRenderer';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { Model } from 'pages/Benchmarking/performance/model';
import {
  createGrowthSeriesChartConfig,
  createTimeSeriesChartConfig,
} from 'pages/Benchmarking/performance/helpers/chartConfigs';
import { Measure } from 'services/model/model.service';
import { BenchmarkEmptyState } from 'components/BenchmarkEmptyState';
import { resolveBenchmarkEmptyStateType } from 'components/benchmarkEmptyStateUtils';
import { MetricsConclusionMessage } from './MetricsConclusionMessage';
import { BenchmarkingTemporalType } from 'components/filter/modules/benchmarking/types';
import { useAppStore } from 'hooks/state/useAppStore';
import { PerformanceSectionType } from 'pages/Benchmarking/performance/model';

interface Props {
  titleId: string;
  performance: Model;
  measure: Measure;
  benchmark: string;
  type: PerformanceSectionType;
  rightComponent?: React.ReactNode;
}

function getAdditionalTitleId(type: string, temporalType: BenchmarkingTemporalType): string {
  const temporalTypId = temporalType.toLocaleLowerCase();
  switch (type) {
    case 'yoy':
      return 'benchmarking.titles.additional.yoy-growth';
    case 'mom':
      return `benchmarking.titles.additional.pop-growth.${temporalTypId}`;
    default:
      return 'general.title.over_time';
  }
}

export function SectionWithCharts({ titleId, performance, measure, benchmark, type, rightComponent = null }: Props) {
  const { name } = useContext(TenantConfigContext);
  const intl = useIntl();
  const baseTestId = `bm-performance-${type}`;

  const { benchmarkingDateRange } = useAppStore();

  const growthSection = type === 'yoy' || type === 'mom';
  const sectionEmptyStateType = resolveBenchmarkEmptyStateType({
    hasTenantData: performance.hasTenantData ?? performance.timeSeries.some(entry => entry.tenantValue !== 0),
    hasTenantHistory:
      performance.hasTenantCompareData ??
      performance.timeSeries.some(entry => entry.tenantCompareValue != null && entry.tenantCompareValue > 0),
    hasPeerData: performance.hasPeerData ?? false,
  });
  const hasPeerGrowthData =
    performance.hasPeerGrowthData ??
    performance.timeSeries.some(
      entry => entry.benchmarkValue != null && entry.benchmarkCompareValue != null && entry.benchmarkCompareValue > 0
    );

  if (growthSection && !hasPeerGrowthData) {
    console.warn('Insufficient benchmark data for Growth Chart. Returning null.');
  }

  const timeSeriesChart = new LineChartRenderer(
    createTimeSeriesChartConfig(performance, measure, name, benchmark, intl)
  );
  const growthChart = growthSection
    ? new ColumnChartRenderer(createGrowthSeriesChartConfig(performance, name, benchmark, intl))
    : null;

  return (
    <>
      {growthSection ? (
        <PageSectionTitle
          additionalTitle={intl.formatMessage({
            id: 'insights.report.marketplace_channel_breakdown.title_subheader',
          })}
          rightComponent={rightComponent}
        >
          <FormattedMessage id={titleId} />
        </PageSectionTitle>
      ) : (
        <PageSectionTitle>
          <FormattedMessage id={titleId} />
        </PageSectionTitle>
      )}

      {sectionEmptyStateType ? (
        <BenchmarkEmptyState type={sectionEmptyStateType} testId={`${baseTestId}-empty-content`} />
      ) : (
        <>
          <BenchmarkMetricsRow metrics={performance.metrics ?? []} type={type} />
          {growthSection && (
            <Tile className="flex flex-row items-center p-0" testId={`${baseTestId}-summary`}>
              <MetricsConclusionMessage
                performance={performance}
                type={type}
                benchmark={benchmark}
                isMoM={type === 'mom'}
                testIdPrefix={`${baseTestId}-summary`}
              />
            </Tile>
          )}
          {growthSection && (
            <Tile
              titleId="benchmarking.performance.ordered-gmv-growth-rate"
              additionalTitle={intl.formatMessage({
                id:
                  type === 'yoy'
                    ? `benchmarking.performance.ordered-gmv-growth-rate.subtitle.${benchmarkingDateRange.temporalType.toLowerCase()}`
                    : `benchmarking.titles.additional.pop.${benchmarkingDateRange.temporalType.toLowerCase()}`,
              })}
              testId={`${baseTestId}-growth`}
            >
              <MetricsChart renderer={growthChart} height="h-[200px]" testId={`${baseTestId}-growth-chart`} />
            </Tile>
          )}
          <Tile
            title={measure.metric.name}
            additionalTitle={intl.formatMessage({ id: getAdditionalTitleId(type, benchmarkingDateRange.temporalType) })}
            testId={`${baseTestId}-trend`}
          >
            <MetricsChart renderer={timeSeriesChart} height="h-[250px]" testId={`${baseTestId}-trend-chart`} />
          </Tile>
        </>
      )}
    </>
  );
}
